import React from "react";
import { fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import FileUpload from "@/components/admin/FileUpload";
import { Plus, Trash2, Copy } from "lucide-react";

// ---------------------------------------------------------------------------
// Reusable CMS field controls (used by every CMS tab).
// NOTE: There is intentionally NO mutual JSX recursion here — LeafField never
// renders FieldRenderer — to avoid the Babel visual-edits plugin choking on a
// component cycle.
// ---------------------------------------------------------------------------

export function TextField({ label, value, onChange, disabled, textarea, rows = 2, testId }) {
  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs text-[var(--text-2)]">{label}</Label>}
      {textarea ? (
        <Textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="bg-[var(--panel)]" data-testid={testId} />
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="bg-[var(--panel)]" data-testid={testId} />
      )}
    </div>
  );
}

export function ImageField({ label, value, onChange, disabled, testId }) {
  return (
    <div className="space-y-2">
      {label && <Label className="text-xs text-[var(--text-2)]">{label}</Label>}
      <div className="flex items-center gap-3">
        <div className="h-16 w-24 rounded bg-[var(--panel-2)] overflow-hidden shrink-0 border border-[var(--border-lch)]">
          {value ? <img src={fileUrl(value)} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder="Image URL" className="bg-[var(--panel)] flex-1" data-testid={testId} />
      </div>
      {!disabled && (
        <FileUpload
          visibility="public"
          category="content"
          multiple={false}
          label="Swap Image"
          testId={`${testId || "cms-img"}-upload`}
          onUploaded={(fs) => fs[0] && onChange(fs[0].url)}
        />
      )}
    </div>
  );
}

export function SelectField({ label, value, onChange, options, disabled, testId }) {
  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs text-[var(--text-2)]">{label}</Label>}
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="bg-[var(--panel)]" data-testid={testId}>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {(options || []).map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`${testId}-opt-${o.value}`}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Leaf (non-list) control renderer.
export function LeafField({ field, value, onChange, disabled, testId }) {
  switch (field.type) {
    case "text":
      return <TextField label={field.label} value={value} onChange={onChange} disabled={disabled} testId={testId} />;
    case "textarea":
      return <TextField label={field.label} value={value} onChange={onChange} disabled={disabled} textarea rows={field.rows || 3} testId={testId} />;
    case "image":
      return <ImageField label={field.label} value={value} onChange={onChange} disabled={disabled} testId={testId} />;
    case "select":
      return <SelectField label={field.label} value={value} onChange={onChange} options={field.options} disabled={disabled} testId={testId} />;
    default:
      return null;
  }
}

// List editor with add / remove / DUPLICATE per item.
export function ListField({ field, value, onChange, disabled }) {
  const items = value || [];
  const setItem = (i, k, v) => { const a = [...items]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  const add = () => onChange([...items, { ...(field.newItem || {}) }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const duplicate = (i) => {
    const copy = JSON.parse(JSON.stringify(items[i]));
    const a = [...items];
    a.splice(i + 1, 0, copy);
    onChange(a);
  };
  const canAdd = !field.max || items.length < field.max;
  const canRemove = !field.min || items.length > field.min;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[var(--text-2)]">{field.label}</Label>
        {!disabled && canAdd && (
          <Button size="sm" variant="outline" onClick={add} data-testid={`cms-add-${field.key}`}>
            <Plus size={13} className="mr-1" />{field.itemLabel || "Item"}
          </Button>
        )}
      </div>
      {items.map((item, i) => (
        <div key={i} className="border border-[var(--border-lch)] rounded p-3 space-y-2 bg-[var(--panel-2)]/60" data-testid={`cms-${field.key}-item-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{field.itemLabel || "Item"} {i + 1}</span>
            {!disabled && (
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => duplicate(i)} disabled={!canAdd} className="text-[var(--text-2)] hover:text-[var(--text)] shrink-0 h-7 px-2 disabled:opacity-30" data-testid={`cms-duplicate-${field.key}-${i}`} title="Duplicate">
                  <Copy size={14} />
                </Button>
                {canRemove && (
                  <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-500 shrink-0 h-7 px-2" data-testid={`cms-remove-${field.key}-${i}`} title="Remove">
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            )}
          </div>
          {field.itemFields.map((sf) => (
            <LeafField
              key={sf.key}
              field={sf}
              value={item[sf.key]}
              onChange={(v) => setItem(i, sf.key, v)}
              disabled={disabled}
              testId={`cms-${field.key}-${i}-${sf.key}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// Top-level field renderer: dispatches list -> ListField, else -> LeafField.
export function FieldRenderer({ field, value, onChange, disabled }) {
  if (field.type === "list") {
    return <ListField field={field} value={value} onChange={onChange} disabled={disabled} />;
  }
  return <LeafField field={field} value={value} onChange={onChange} disabled={disabled} testId={`cms-field-${field.key}`} />;
}
